import React, { useState } from 'react';
import {
  Page,
  Card,
  Layout,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Divider,
  Badge,
} from '@shopify/polaris';
import { CodeIcon, ProductIcon, StarIcon } from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';

const ThemeSetup: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'product' | 'listing'>('product');

  const productPageSteps = [
    {
      number: 1,
      title: 'Acesse o Theme Editor',
      description: 'No admin do Shopify, vá em Online Store > Themes e clique em "Customize" no tema ativo.',
      icon: CodeIcon,
    },
    {
      number: 2,
      title: 'Abra uma Página de Produto',
      description: 'No editor, navegue até uma página de produto qualquer para visualizar a estrutura.',
      icon: ProductIcon,
    },
    {
      number: 3,
      title: 'Adicione o Bloco de Avaliações',
      description: 'Clique em "Add block" ou "Add section" e procure por "Reviews Widget". Clique para adicionar.',
      icon: StarIcon,
    },
    {
      number: 4,
      title: 'Configure o Bloco',
      description: 'Ajuste as configurações: título da seção, mostrar resumo, avaliações por página e ordem de exibição.',
      icon: CodeIcon,
    },
    {
      number: 5,
      title: 'Salve e Publique',
      description: 'Clique em "Save" no canto superior direito. As avaliações aparecerão automaticamente na loja.',
      icon: CodeIcon,
    },
  ];

  const listingSteps = [
    {
      number: 1,
      title: 'Acesse o Theme Editor',
      description: 'No admin do Shopify, vá em Online Store > Themes e clique em "Customize" no tema ativo.',
      icon: CodeIcon,
    },
    {
      number: 2,
      title: 'Abra uma Página de Coleção',
      description: 'No editor, navegue até uma página de coleção (listagem de produtos).',
      icon: ProductIcon,
    },
    {
      number: 3,
      title: 'Adicione o Bloco de Resumo',
      description: 'Na seção de informações do produto, clique em "Add block" e procure por "Reviews Summary".',
      icon: StarIcon,
    },
    {
      number: 4,
      title: 'Posicione o Bloco',
      description: 'Arraste o bloco para posicioná-lo ao lado do preço ou abaixo do título do produto.',
      icon: CodeIcon,
    },
    {
      number: 5,
      title: 'Salve e Publique',
      description: 'Clique em "Save". As estrelas aparecerão automaticamente na listagem de produtos.',
      icon: CodeIcon,
    },
  ];

  return (
    <Page
      title={t('themeSetup.title', { defaultValue: 'Configurar Tema' })}
      subtitle={t('themeSetup.subtitle', { defaultValue: 'Adicione avaliações de produtos ao seu tema em poucos passos' })}
    >
      <Layout>
        <Layout.Section>
          <Banner tone="info">
            <Text as="p" variant="bodyMd">
              <strong>{t('themeSetup.banner', { defaultValue: '💡 Dica:' })}</strong>{' '}
              {t('themeSetup.bannerText', {
                defaultValue:
                  'Os blocos são adicionados diretamente no Theme Editor. Não é necessário editar código manualmente!',
              })}
            </Text>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              {/* Tabs */}
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  borderBottom: '2px solid #E5E7EB',
                  marginBottom: '24px',
                }}
              >
                <button
                  onClick={() => setActiveTab('product')}
                  style={{
                    padding: '12px 24px',
                    background: activeTab === 'product' ? '#111827' : 'transparent',
                    color: activeTab === 'product' ? 'white' : '#374151',
                    border: 'none',
                    borderBottom: activeTab === 'product' ? '3px solid #111827' : '3px solid transparent',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'all 0.2s',
                    borderRadius: '8px 8px 0 0',
                  }}
                >
                  {t('themeSetup.tabProduct', { defaultValue: '📄 Página do Produto' })}
                </button>
                <button
                  onClick={() => setActiveTab('listing')}
                  style={{
                    padding: '12px 24px',
                    background: activeTab === 'listing' ? '#111827' : 'transparent',
                    color: activeTab === 'listing' ? 'white' : '#374151',
                    border: 'none',
                    borderBottom: activeTab === 'listing' ? '3px solid #111827' : '3px solid transparent',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'all 0.2s',
                    borderRadius: '8px 8px 0 0',
                  }}
                >
                  {t('themeSetup.tabListing', { defaultValue: '⭐ Estrelas na Listagem' })}
                </button>
              </div>

              {activeTab === 'product' ? (
                <BlockStack gap="500">
                  <BlockStack gap="300">
                    <Text variant="headingLg" as="h2">
                      {t('themeSetup.productTitle', { defaultValue: 'Adicionar Avaliações na Página do Produto' })}
                    </Text>
                    <Text as="p" tone="subdued">
                      {t('themeSetup.productDescription', {
                        defaultValue:
                          'Adicione um bloco completo de avaliações que mostra resumo, distribuição de notas e todas as avaliações dos clientes.',
                      })}
                    </Text>
                  </BlockStack>

                  <Divider />

                  <BlockStack gap="400">
                    {productPageSteps.map((step) => (
                      <Card key={step.number} background="bg-surface-secondary">
                        <BlockStack gap="300">
                          <InlineStack align="start" gap="400">
                            <div
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                              }}
                            >
                              <Text
                                as="span"
                                variant="headingLg"
                                fontWeight="bold"
                                tone="subdued"
                              >
                                <span style={{ color: 'white' }}>{step.number}</span>
                              </Text>
                            </div>
                            <div style={{ flex: 1 }}>
                              <BlockStack gap="200">
                                <Text variant="headingSm" as="h3" fontWeight="semibold">
                                  {step.title}
                                </Text>
                                <Text as="p" tone="subdued" variant="bodyMd">
                                  {step.description}
                                </Text>
                              </BlockStack>
                            </div>
                          </InlineStack>
                        </BlockStack>
                      </Card>
                    ))}
                  </BlockStack>

                  <Divider />

                  <Card background="bg-surface-success">
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3" fontWeight="semibold">
                        {t('themeSetup.productResult', { defaultValue: 'Resultado Final' })}
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {t('themeSetup.productResultText', {
                          defaultValue:
                            'Na página do produto, os clientes verão: resumo de avaliações com nota média, distribuição de notas (5 estrelas, 4 estrelas, etc), lista completa de avaliações com fotos, e formulário para deixar nova avaliação.',
                        })}
                      </Text>
                    </BlockStack>
                  </Card>
                </BlockStack>
              ) : (
                <BlockStack gap="500">
                  <BlockStack gap="300">
                    <Text variant="headingLg" as="h2">
                      {t('themeSetup.listingTitle', { defaultValue: 'Adicionar Estrelas na Listagem de Produtos' })}
                    </Text>
                    <Text as="p" tone="subdued">
                      {t('themeSetup.listingDescription', {
                        defaultValue:
                          'Adicione estrelas e nota média ao lado de cada produto na listagem de coleções. Perfeito para aumentar a confiança e conversão!',
                      })}
                    </Text>
                  </BlockStack>

                  <Divider />

                  <BlockStack gap="400">
                    {listingSteps.map((step) => (
                      <Card key={step.number} background="bg-surface-secondary">
                        <BlockStack gap="300">
                          <InlineStack align="start" gap="400">
                            <div
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)',
                              }}
                            >
                              <Text
                                as="span"
                                variant="headingLg"
                                fontWeight="bold"
                                tone="subdued"
                              >
                                <span style={{ color: 'white' }}>{step.number}</span>
                              </Text>
                            </div>
                            <div style={{ flex: 1 }}>
                              <BlockStack gap="200">
                                <Text variant="headingSm" as="h3" fontWeight="semibold">
                                  {step.title}
                                </Text>
                                <Text as="p" tone="subdued" variant="bodyMd">
                                  {step.description}
                                </Text>
                              </BlockStack>
                            </div>
                          </InlineStack>
                        </BlockStack>
                      </Card>
                    ))}
                  </BlockStack>

                  <Divider />

                  <Card background="bg-surface-success">
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3" fontWeight="semibold">
                        {t('themeSetup.listingResult', { defaultValue: 'Resultado Final' })}
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {t('themeSetup.listingResultText', {
                          defaultValue:
                            'Na listagem de produtos, cada item mostrará: nota média (ex: 4.5/5), estrelas visuais, contagem de avaliações (ex: "12 reviews"), e link para ver todas as avaliações. O widget carrega automaticamente e só aparece se o produto tiver avaliações.',
                        })}
                      </Text>
                    </BlockStack>
                  </Card>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3" fontWeight="semibold">
                {t('themeSetup.helpTitle', { defaultValue: 'Precisa de Ajuda?' })}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t('themeSetup.helpText', {
                  defaultValue:
                    'Se os blocos não aparecerem no Theme Editor, certifique-se de que a extensão foi instalada corretamente.',
                })}
              </Text>
              <Button
                variant="secondary"
                onClick={() => window.open('https://help.shopify.com', '_blank')}
              >
                {t('themeSetup.helpButton', { defaultValue: 'Ver Documentação Shopify' })}
              </Button>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3" fontWeight="semibold">
                {t('themeSetup.featuresTitle', { defaultValue: 'Recursos dos Blocos' })}
              </Text>
              <BlockStack gap="200">
                <InlineStack align="start" gap="200">
                  <Badge tone="success">✓</Badge>
                  <Text as="p" variant="bodyMd">
                    {t('themeSetup.feature1', { defaultValue: 'Carregamento automático' })}
                  </Text>
                </InlineStack>
                <InlineStack align="start" gap="200">
                  <Badge tone="success">✓</Badge>
                  <Text as="p" variant="bodyMd">
                    {t('themeSetup.feature2', { defaultValue: 'Responsivo e mobile' })}
                  </Text>
                </InlineStack>
                <InlineStack align="start" gap="200">
                  <Badge tone="success">✓</Badge>
                  <Text as="p" variant="bodyMd">
                    {t('themeSetup.feature3', { defaultValue: 'Fotos e avaliações verificadas' })}
                  </Text>
                </InlineStack>
                <InlineStack align="start" gap="200">
                  <Badge tone="success">✓</Badge>
                  <Text as="p" variant="bodyMd">
                    {t('themeSetup.feature4', { defaultValue: 'Personalizável no Theme Editor' })}
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default ThemeSetup;

